import React from 'react'
import {
  createEditor as createSlateEditor,
  Descendant,
  Editor,
  Element as SlateElement,
  Text,
} from 'slate'
import { withHistory } from 'slate-history'
import { withReact } from 'slate-react'

import { MentionItem } from '@/components/MarkdownDisplay'

import { MENTION_PATTERN_WITH_CAPTURE_REGEX } from '../constants'
import type { CustomElement } from '../types/slate'

export const createEditor = (): Editor => {
  return withHistory(withReact(createSlateEditor()))
}

export const initialEditorValue: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  } as CustomElement,
]

export const serializeToPlainText = (nodes: Descendant[]): string => {
  const serializeNode = (node: Descendant): string => {
    if (Text.isText(node)) {
      return node.text
    }

    if (SlateElement.isElement(node)) {
      // For mention elements, return @username format
      if (node.type === 'mention') {
        const mentionNode = node as CustomElement & { userName?: string }
        return `@${mentionNode.userName || ''}`
      }

      // For other elements, recursively serialize children
      return node.children.map((child) => serializeNode(child)).join('')
    }

    return ''
  }

  return nodes.map((node) => serializeNode(node)).join('\n')
}

export const serializeToMarkdown = (nodes: Descendant[]): string => {
  return nodes.map((node) => serializeNodeToMarkdown(node)).join('\n\n')
}

export const markdownToPlainText = (markdown: string): string => {
  if (!markdown || typeof markdown !== 'string') {
    return ''
  }

  // First, convert mention patterns @[username](user:userId) to @username
  // This must be done before removing other link patterns
  // Use capture group to extract username
  let text = markdown.replace(MENTION_PATTERN_WITH_CAPTURE_REGEX, '@$1')

  // Remove markdown formatting
  text = text
    .replace(/(\*\*|__)(.*?)\1/g, '$2') // NOSONAR - Bold regex
    .replace(/(\*|_)(.*?)\1/g, '$2') // NOSONAR - Italic regex
    .replace(/`(.*?)`/g, '$1') // NOSONAR - Inline code regex
    .replace(/```[\s\S]*?```/g, '') // NOSONAR - Code blocks regex
    .replace(/!\[.*?\]\(.*?\)/g, '') // NOSONAR - Images regex
    .replace(/\[([^\]]*?)\]\([^)]*?\)/g, '$1') // NOSONAR - Links (extract text only) regex
    .replace(/#+\s(.*?)/g, '$1') // NOSONAR - Headings regex
    .replace(/>\s(.*?)/g, '$1') // NOSONAR - Blockquotes regex
    .replace(/-\s(.*?)/g, '$1') // NOSONAR - Bullet lists regex
    .replace(/\d+\.\s(.*?)/g, '$1') // NOSONAR - Numbered lists regex
    .replace(/\n{2,}/g, '\n') // NOSONAR - Multiple newlines regex
    .trim()

  return text
}

type MentionInfo = {
  index: number
  length: number
  userName: string
  userId: string
  placeholder: string
}

// Extract mentions from markdown and create placeholders
const extractMentionsWithPlaceholders = (markdown: string): MentionInfo[] => {
  const mentionRegex = new RegExp(MENTION_PATTERN_WITH_CAPTURE_REGEX)
  const mentions: MentionInfo[] = []
  let match: RegExpExecArray | null
  let placeholderCounter = 0
  mentionRegex.lastIndex = 0

  while ((match = mentionRegex.exec(markdown)) !== null) {
    const placeholder = `MENTIONPLACEHOLDER${placeholderCounter++}XYZ`
    mentions.push({
      index: match.index,
      length: match[0].length,
      userName: match[1],
      userId: match[2],
      placeholder,
    })
  }

  return mentions
}

// Replace mentions with placeholders in text
const replaceMentionsWithPlaceholders = (
  text: string,
  mentions: MentionInfo[]
): string => {
  let textWithPlaceholders = text
  // Process in reverse order to maintain indices
  for (let i = mentions.length - 1; i >= 0; i--) {
    const mention = mentions[i]
    textWithPlaceholders =
      textWithPlaceholders.substring(0, mention.index) +
      mention.placeholder +
      textWithPlaceholders.substring(mention.index + mention.length)
  }
  return textWithPlaceholders
}

// Process markdown while protecting placeholders
const processMarkdownWithPlaceholderProtection = (
  textWithPlaceholders: string,
  mentions: MentionInfo[]
): string => {
  // Protect placeholders by temporarily replacing them with a safe format
  const placeholderMap = new Map<string, string>()
  let safeText = textWithPlaceholders
  mentions.forEach((mention, idx) => {
    const safePlaceholder = `MENTIONPLACEHOLDER${idx}SAFE`
    placeholderMap.set(safePlaceholder, mention.placeholder)
    safeText = safeText.replace(mention.placeholder, safePlaceholder)
  })

  // Process markdown to plain text
  let processedText = safeText
    .replace(/(\*\*|__)(.*?)\1/g, '$2') // NOSONAR - Bold regex
    .replace(/(\*|_)(.*?)\1/g, '$2') // NOSONAR - Italic regex
    .replace(/`(.*?)`/g, '$1') // NOSONAR - Inline code regex
    .replace(/```[\s\S]*?```/g, '') // NOSONAR - Code blocks regex
    .replace(/!\[.*?\]\(.*?\)/g, '') // NOSONAR - Images regex
    .replace(/\[([^\]]*?)\]\([^)]*?\)/g, '$1') // NOSONAR - Links (extract text only) regex
    .replace(/#+\s(.*?)/g, '$1') // NOSONAR - Headings regex
    .replace(/>\s(.*?)/g, '$1') // NOSONAR - Blockquotes regex
    .replace(/-\s(.*?)/g, '$1') // NOSONAR - Bullet lists regex
    .replace(/\d+\.\s(.*?)/g, '$1') // NOSONAR - Numbered lists regex
    .replace(/\n{2,}/g, '\n') // NOSONAR - Multiple newlines regex
    .trim()

  // Restore original placeholders
  placeholderMap.forEach((originalPlaceholder, safePlaceholder) => {
    processedText = processedText.replace(safePlaceholder, originalPlaceholder)
  })

  return processedText
}

// Build result array with MentionItem components
const buildResultWithMentions = (
  processedText: string,
  mentions: MentionInfo[]
): Array<string | React.ReactElement> => {
  const result: Array<string | React.ReactElement> = []
  let lastIndex = 0
  let keyCounter = 0

  for (const mention of mentions) {
    const placeholderIndex = processedText.indexOf(
      mention.placeholder,
      lastIndex
    )

    if (placeholderIndex === -1) {
      continue
    }

    // Add text before placeholder
    if (placeholderIndex > lastIndex) {
      const textBefore = processedText.substring(lastIndex, placeholderIndex)
      if (textBefore) {
        result.push(textBefore)
      }
    }

    // Add MentionItem component
    result.push(
      React.createElement(MentionItem, {
        key: `mention-${keyCounter++}-${placeholderIndex}`,
        userName: mention.userName,
        userId: mention.userId,
      })
    )

    lastIndex = placeholderIndex + mention.placeholder.length
  }

  // Add remaining text after last placeholder
  if (lastIndex < processedText.length) {
    const textAfter = processedText.substring(lastIndex)
    if (textAfter) {
      result.push(textAfter)
    }
  }

  return result.length > 0 ? result : [processedText]
}

/**
 * Enhanced version of markdownToPlainText that highlights mention items in the UI.
 * Returns an array of React elements (strings and MentionItem components) instead of plain text.
 *
 * @param markdown - The markdown string to convert
 * @returns Array of React elements (strings and MentionItem components) for rendering
 */
export const markdownToPlainTextWithMentions = (
  markdown: string
): Array<string | React.ReactElement> => {
  if (!markdown || typeof markdown !== 'string') {
    return ['']
  }

  const mentions = extractMentionsWithPlaceholders(markdown)

  if (mentions.length === 0) {
    return [markdownToPlainText(markdown)]
  }

  const textWithPlaceholders = replaceMentionsWithPlaceholders(
    markdown,
    mentions
  )
  const processedText = processMarkdownWithPlaceholderProtection(
    textWithPlaceholders,
    mentions
  )

  return buildResultWithMentions(processedText, mentions)
}

type Token = {
  start: number
  end: number
  type: 'bold' | 'italic' | 'code' | 'mention'
  content: string
  userId?: string
  userName?: string
}

type Range = { start: number; end: number }

// Helper function to check if two ranges overlap
const rangesOverlap = (
  start1: number,
  end1: number,
  start2: number,
  end2: number
): boolean => {
  return (start1 < end2 && end1 > start2) || (start2 < end1 && end2 > start1)
}

// Helper function to check if a match overlaps with tokens
const overlapsWithTokens = (
  match: RegExpExecArray,
  tokens: Token[]
): boolean => {
  return tokens.some((token) =>
    rangesOverlap(
      match.index,
      match.index + match[0].length,
      token.start,
      token.end
    )
  )
}

// Helper function to check if a match overlaps with ranges
const overlapsWithRanges = (
  match: RegExpExecArray,
  ranges: Range[]
): boolean => {
  return ranges.some((range) =>
    rangesOverlap(
      match.index,
      match.index + match[0].length,
      range.start,
      range.end
    )
  )
}

// Extract mention tokens from text
const extractMentions = (unescapedText: string): Token[] => {
  const tokens: Token[] = []
  const mentionRegex = new RegExp(MENTION_PATTERN_WITH_CAPTURE_REGEX)
  let match: RegExpExecArray | null
  while ((match = mentionRegex.exec(unescapedText)) !== null) {
    tokens.push({
      start: match.index,
      end: match.index + match[0].length,
      type: 'mention',
      content: match[0],
      userId: match[2],
      userName: match[1],
    })
  }
  return tokens
}

// Extract nested bold+italic matches
const extractNestedBoldItalic = (
  unescapedText: string,
  tokens: Token[]
): { matches: RegExpExecArray[]; ranges: Range[] } => {
  const nestedBoldItalicRegex = /\*\*\*(.+?)\*\*\*/g
  const matches: RegExpExecArray[] = []
  const ranges: Range[] = []
  let match: RegExpExecArray | null
  while ((match = nestedBoldItalicRegex.exec(unescapedText)) !== null) {
    if (!overlapsWithTokens(match, tokens)) {
      matches.push(match)
      ranges.push({
        start: match.index,
        end: match.index + match[0].length,
      })
    }
  }
  return { matches, ranges }
}

// Extract bold tokens
const extractBold = (
  unescapedText: string,
  tokens: Token[],
  nestedRanges: Range[]
): void => {
  const boldRegex = /(\*\*|__)((?:(?!\1).)+?)\1/g
  let match: RegExpExecArray | null
  while ((match = boldRegex.exec(unescapedText)) !== null) {
    if (
      !overlapsWithTokens(match, tokens) &&
      !overlapsWithRanges(match, nestedRanges)
    ) {
      tokens.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'bold',
        content: match[2],
      })
    }
  }
}

// Extract italic tokens
const extractItalic = (
  unescapedText: string,
  tokens: Token[],
  nestedRanges: Range[]
): void => {
  const italicRegex = /(?<![*_])([*_])(?![*_])([^*_]+?)\1(?![*_])/g
  let match: RegExpExecArray | null
  while ((match = italicRegex.exec(unescapedText)) !== null) {
    if (
      !overlapsWithTokens(match, tokens) &&
      !overlapsWithRanges(match, nestedRanges)
    ) {
      tokens.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'italic',
        content: match[2],
      })
    }
  }
}

// Extract code tokens
const extractCode = (unescapedText: string, tokens: Token[]): void => {
  const codeRegex = /`([^`]+?)`/g
  let match: RegExpExecArray | null
  while ((match = codeRegex.exec(unescapedText)) !== null) {
    if (!overlapsWithTokens(match, tokens)) {
      tokens.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'code',
        content: match[1],
      })
    }
  }
}

// Process nested bold+italic matches
const processNestedMatches = (
  nestedMatches: RegExpExecArray[],
  unescapedText: string
): Descendant[] => {
  const result: Descendant[] = []
  let lastIndex = 0
  for (const nestedMatch of nestedMatches) {
    if (nestedMatch.index > lastIndex) {
      const beforeText = unescapedText.slice(lastIndex, nestedMatch.index)
      if (beforeText) {
        const parsedBefore = parseInlineFormats(beforeText)
        result.push(...parsedBefore)
      }
    }
    result.push({
      text: nestedMatch[1],
      bold: true,
      italic: true,
    })
    lastIndex = nestedMatch.index + nestedMatch[0].length
  }
  if (lastIndex < unescapedText.length) {
    const remainingText = unescapedText.slice(lastIndex)
    if (remainingText) {
      const parsedRemaining = parseInlineFormats(remainingText)
      result.push(...parsedRemaining)
    }
  }
  return result
}

// Create a mention element from a token
const createMentionElement = (token: Token): CustomElement => {
  return {
    type: 'mention',
    userId: token.userId || '',
    userName: token.userName || '',
    userEmail: '',
    children: [{ text: '' }],
  }
}

// Create a formatted text node from a token
const createFormattedNode = (token: Token): Text => {
  const formattedNode: Text = { text: token.content }
  if (token.type === 'bold') {
    formattedNode.bold = true
  } else if (token.type === 'italic') {
    formattedNode.italic = true
  } else if (token.type === 'code') {
    formattedNode.code = true
  }
  return formattedNode
}

// Build result from tokens
const buildResultFromTokens = (
  tokens: Token[],
  unescapedText: string
): Descendant[] => {
  const result: Descendant[] = []
  let lastIndex = 0
  for (const token of tokens) {
    if (token.start > lastIndex) {
      const plainText = unescapedText.slice(lastIndex, token.start)
      if (plainText) {
        result.push({ text: plainText })
      }
    }

    if (token.type === 'mention') {
      result.push(createMentionElement(token))
    } else {
      result.push(createFormattedNode(token))
    }

    lastIndex = token.end
  }

  if (lastIndex < unescapedText.length) {
    const remainingText = unescapedText.slice(lastIndex)
    if (remainingText) {
      result.push({ text: remainingText })
    }
  }

  return result.length > 0 ? result : [{ text: unescapedText }]
}

const parseInlineFormats = (text: string): Descendant[] => {
  if (typeof text !== 'string' || text === '') {
    return [{ text: '' }]
  }

  const unescapedText = text.replace(/\\([*_`])/g, '$1') // NOSONAR

  // Extract all tokens
  const tokens = extractMentions(unescapedText)
  const { matches: nestedMatches, ranges: nestedRanges } =
    extractNestedBoldItalic(unescapedText, tokens)
  extractBold(unescapedText, tokens, nestedRanges)
  extractItalic(unescapedText, tokens, nestedRanges)
  extractCode(unescapedText, tokens)

  // Handle nested matches first
  if (nestedMatches.length > 0) {
    return processNestedMatches(nestedMatches, unescapedText)
  }

  // Sort tokens by start position
  tokens.sort((a, b) => a.start - b.start)

  // Build result from tokens
  if (tokens.length === 0) {
    return [{ text: unescapedText }]
  }

  return buildResultFromTokens(tokens, unescapedText)
}

export const cleanMarkdownLine = (line: string): { isEmpty: boolean } => {
  if (!line || typeof line !== 'string') return { isEmpty: true }

  const trimmed = line.trim()

  // if no character alphanumeric
  const hasAlphanumeric = /[a-zA-Z0-9]/.test(trimmed)
  if (hasAlphanumeric) return { isEmpty: false }

  // eslint-disable-next-line no-useless-escape
  const markdownCharsOnly = /^[*\#>\-\_\~`]+$/.test(trimmed)
  if (markdownCharsOnly) {
    return { isEmpty: true }
  }

  return { isEmpty: false }
}

const BULLETED_LIST_REGEX = /^-\s+/
const NUMBERED_LIST_REGEX = /^\d+\.\s+/
// Limit heading and blockquote content to 500 chars to avoid ReDoS
const HEADING_REGEX = /^(#{1,6})\s+(.{1,500})$/
const BLOCKQUOTE_REGEX = /^>\s+(.{1,500})$/

const parseBlockElement = (
  line: string,
  isListItem?: boolean
): CustomElement => {
  if (typeof line !== 'string') {
    return {
      type: 'paragraph',
      children: [{ text: '' }],
    } as CustomElement
  }
  const cleaned = cleanMarkdownLine(line)
  if (cleaned.isEmpty) {
    return {
      type: 'paragraph',
      children: [{ text: '' }],
    }
  }

  if (line.trim() === '') {
    return {
      type: 'paragraph',
      children: [{ text: '' }],
    } as CustomElement
  }

  const headingMatch = HEADING_REGEX.exec(line)
  if (headingMatch) {
    const level = headingMatch[1].length as 1 | 2 | 3 | 4 | 5 | 6
    const text = headingMatch[2]
    return {
      type: 'heading',
      level,
      children: parseInlineFormats(text),
    } as CustomElement
  }

  const blockquoteMatch = BLOCKQUOTE_REGEX.exec(line)
  if (blockquoteMatch) {
    const text = blockquoteMatch[1]
    return {
      type: 'block-quote',
      children: parseInlineFormats(text),
    } as CustomElement
  }

  const bulletedListMatch = BULLETED_LIST_REGEX.exec(line)
  if (bulletedListMatch && isListItem) {
    const text = line.replace(BULLETED_LIST_REGEX, '')
    return {
      type: 'list-item',
      children: parseInlineFormats(text),
    } as CustomElement
  }

  const numberedListMatch = NUMBERED_LIST_REGEX.exec(line)
  if (numberedListMatch && isListItem) {
    const text = line.replace(NUMBERED_LIST_REGEX, '')
    return {
      type: 'list-item',
      children: parseInlineFormats(text),
    } as CustomElement
  }

  return {
    type: 'paragraph',
    children: parseInlineFormats(line),
  } as CustomElement
}

const serializeNodeToMarkdown = (node: Descendant): string => {
  if (Text.isText(node)) {
    let text = node.text
    // Handle combined formats first
    if (node.bold && node.italic) {
      // Both bold and italic: use ***text*** format
      text = `***${text}***`
    } else if (node.bold) {
      text = `**${text}**`
    } else if (node.italic) {
      text = `*${text}*`
    }
    if (node.code) {
      text = `\`${text}\``
    }
    return text
  }

  if (!SlateElement.isElement(node)) {
    return ''
  }

  const children = node.children
    .map((child) => serializeNodeToMarkdown(child))
    .join('')

  switch (node.type) {
    case 'paragraph':
      return children
    case 'heading':
      return `${'#'.repeat(node.level)} ${children}`
    case 'block-quote':
      return `> ${children}`
    case 'bulleted-list':
      return node.children
        .map((child) => {
          const text = serializeNodeToMarkdown(child)
          return `- ${text}`
        })
        .join('\n')
    case 'numbered-list':
      return node.children
        .map((child, index) => {
          const text = serializeNodeToMarkdown(child)
          return `${index + 1}. ${text}`
        })
        .join('\n')
    case 'list-item':
      return children
    case 'mention': {
      const mentionNode = node as CustomElement & {
        userId?: string
        userName?: string
      }
      // Use format: @[username](user:userId) for parsing back
      const userId = mentionNode.userId || ''
      const userName = mentionNode.userName || ''
      return `@[${userName}](user:${userId})`
    }
    default:
      return children
  }
}

type ListState = {
  currentList: CustomElement | null
  currentListType: 'bulleted' | 'numbered' | null
}

const processBulletedListBlock = (
  lines: string[],
  listState: ListState,
  result: Descendant[]
): void => {
  const listItems = lines
    .filter((line) => BULLETED_LIST_REGEX.exec(line))
    .map((line) => parseBlockElement(line, true))

  if (listState.currentListType === 'bulleted' && listState.currentList) {
    listState.currentList.children.push(...listItems)
  } else {
    if (listState.currentList) {
      result.push(listState.currentList)
    }
    listState.currentList = {
      type: 'bulleted-list',
      children: listItems,
    } as CustomElement
    listState.currentListType = 'bulleted'
  }
}

const processNumberedListBlock = (
  lines: string[],
  listState: ListState,
  result: Descendant[]
): void => {
  const listItems = lines
    .filter((line) => NUMBERED_LIST_REGEX.exec(line))
    .map((line) => parseBlockElement(line, true))

  if (listState.currentListType === 'numbered' && listState.currentList) {
    listState.currentList.children.push(...listItems)
  } else {
    if (listState.currentList) {
      result.push(listState.currentList)
    }
    listState.currentList = {
      type: 'numbered-list',
      children: listItems,
    } as CustomElement
    listState.currentListType = 'numbered'
  }
}

const finalizeCurrentList = (
  listState: ListState,
  result: Descendant[]
): void => {
  if (listState.currentList) {
    result.push(listState.currentList)
    listState.currentList = null
    listState.currentListType = null
  }
}

const processRegularBlock = (
  block: string,
  listState: ListState,
  result: Descendant[]
): void => {
  finalizeCurrentList(listState, result)
  const element = parseBlockElement(block, false)
  if (!isEmptyStringElement(element)) {
    result.push(element)
  }
}

const validateAndSplitMarkdown = (markdown: string): string[] | null => {
  if (!markdown || typeof markdown !== 'string' || markdown.trim() === '') {
    return null
  }

  const blocks = markdown
    .split(/\n\n+/)
    .map((block) => block.trim())
    .filter(Boolean)

  return blocks.length === 0 ? null : blocks
}

export const parseMarkdownToSlate = (markdown: string): Descendant[] => {
  try {
    const blocks = validateAndSplitMarkdown(markdown)
    if (!blocks) {
      return initialEditorValue
    }

    const result: Descendant[] = []
    const listState: ListState = {
      currentList: null,
      currentListType: null,
    }

    for (const block of blocks) {
      const lines = block.split('\n').filter(Boolean)
      const firstLine = lines[0]

      const isBulletedList = firstLine
        ? BULLETED_LIST_REGEX.exec(firstLine)
        : null
      const isNumberedList = firstLine
        ? NUMBERED_LIST_REGEX.exec(firstLine)
        : null

      if (isBulletedList) {
        processBulletedListBlock(lines, listState, result)
      } else if (isNumberedList) {
        processNumberedListBlock(lines, listState, result)
      } else {
        processRegularBlock(block, listState, result)
      }
    }

    if (listState.currentList) {
      result.push(listState.currentList)
    }

    return result.length > 0 ? result : initialEditorValue
  } catch {
    return initialEditorValue
  }
}

const isEmptyStringElement = (element: CustomElement): boolean => {
  const { type, children } = element
  if (type === 'paragraph' && children.length === 1) {
    const child = children[0]
    if (Text.isText(child) && child.text.trim() === '') {
      return true
    }
  }
  return false
}

export const countCharacters = (nodes: Descendant[]): number => {
  let count = 0

  const countNode = (node: Descendant): void => {
    if (Text.isText(node)) {
      count += node.text.length
    } else if (SlateElement.isElement(node)) {
      // For mention elements, count the display name (including @)
      if (node.type === 'mention') {
        const mentionNode = node as CustomElement & { userName?: string }
        const mentionText = `@${mentionNode.userName || ''}`
        count += mentionText.length
      } else {
        // Recursively count children
        node.children.forEach(countNode)
      }
    }
  }

  nodes.forEach(countNode)
  return count
}

export const toggleMark = (editor: Editor, format: string): void => {
  const marks = Editor.marks(editor)
  const isActive = marks ? marks[format as keyof typeof marks] === true : false

  if (isActive) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

export interface InsertMarkdownResult {
  newText: string
  newCursorPosition: number
}

/**
 * Inserts markdown syntax at the cursor position in a textarea
 * @param rawMarkdown - The current markdown text
 * @param syntax - The markdown syntax to insert (e.g., '**', '*', '# ')
 * @param wrapText - Whether to wrap selected text with the syntax
 * @param selectionStart - The start position of the selection
 * @param selectionEnd - The end position of the selection
 * @param maxCharacters - Maximum allowed characters
 * @returns Object containing the new text and cursor position, or null if limit exceeded
 */
export const insertMarkdownSyntax = (
  rawMarkdown: string,
  syntax: string,
  wrapText: boolean,
  selectionStart: number,
  selectionEnd: number,
  maxCharacters: number
): InsertMarkdownResult | null => {
  const selectedText = rawMarkdown.substring(selectionStart, selectionEnd)
  let newText: string

  if (wrapText && selectedText) {
    // Wrap selected text with syntax (e.g., **selected**)
    newText =
      rawMarkdown.substring(0, selectionStart) +
      syntax +
      selectedText +
      syntax +
      rawMarkdown.substring(selectionEnd)
  } else if (wrapText) {
    // Insert syntax with cursor in middle (e.g., **|**)
    newText =
      rawMarkdown.substring(0, selectionStart) +
      syntax +
      syntax +
      rawMarkdown.substring(selectionEnd)
  } else {
    // Insert prefix syntax (e.g., # )
    newText =
      rawMarkdown.substring(0, selectionStart) +
      syntax +
      rawMarkdown.substring(selectionEnd)
  }

  // Check character limit
  if (newText.length > maxCharacters) {
    return null
  }

  // Calculate cursor position
  let newCursorPosition: number
  if (wrapText && !selectedText) {
    // Place cursor between the syntax markers
    newCursorPosition = selectionStart + syntax.length
  } else if (wrapText) {
    // Place cursor after wrapped text
    newCursorPosition =
      selectionStart + syntax.length + selectedText.length + syntax.length
  } else {
    // Place cursor after inserted syntax
    newCursorPosition = selectionStart + syntax.length
  }

  return {
    newText,
    newCursorPosition,
  }
}
